import { IOrderItem } from '@/interfaces/IOrderItem';
import { ComandaSplit } from '@/enums/ComandaSplit';

/**
 * Pure planning for split comanda documents: a station's line batch becomes
 * one grouped document (the lines that print together, exactly as before the
 * feature) plus, for every line whose catalog item has a `comandaSplit` mode,
 * one document per unit (`unit`) or one document per line (`line`, i.e. per
 * group of identical units — the POS cart already merges identical
 * configurations into one line). No Vue/store imports (same style as
 * auto-print-policy.ts and group-print-plan.ts).
 */

/** One separate comanda document: an amount-1 copy (unit mode) or the line as received (line mode). */
export interface IComandaSeparateDoc {
  /**
   * Unit mode: `{ ...line, amount: 1 }`; line mode: the line itself. Either
   * keeps index, options, comment, item and the addedAt/modifiedAt stamps.
   */
  line: IOrderItem;
  index: number;
  /** Units this document accounts for: 1 (unit) or the line's passed amount (line). */
  units: number;
  /**
   * `n/total` for unit documents (absolute position within the ORIGINAL line
   * amount, 1-based); null for line documents (they read `Nx Name`).
   */
  marker: string | null;
  /** Dedupe suffix under the station's base key: `${index}.${unitNo}` (unit) or `${index}.L` (line). */
  keySuffix: string;
}

export interface IComandaDocPlan {
  /**
   * Lines that print together: SAME references and SAME order as received
   * (canceled lines included — the renderer skips them, byte parity).
   */
  grouped: IOrderItem[];
  /**
   * Separate documents of split, non-canceled lines, in batch line order: a
   * unit line expands to its units (ascending), a line-mode line to ONE
   * document.
   */
  separate: IComandaSeparateDoc[];
}

export function planComandaStationDocs({
  lines,
  splitOf,
  originalAmountOf,
}: {
  /**
   * The station's batch exactly as printOrder hands it (delta copies for
   * partially printed lines, original references otherwise — never mutated).
   */
  lines: IOrderItem[];
  splitOf: (line: IOrderItem) => ComandaSplit;
  /** Original amount of the line in the order (lookup by index); falls back to the copy's amount. */
  originalAmountOf: (line: IOrderItem) => number;
}): IComandaDocPlan {
  // A line without `index` can never be a separate document (marking and
  // options are keyed by index): it stays on the grouped document, never
  // dropped.
  const isSeparate = (line: IOrderItem): boolean =>
    splitOf(line) !== ComandaSplit.None && line.index !== undefined;

  const grouped = lines.filter((line) => !isSeparate(line));
  const separate: IComandaSeparateDoc[] = [];

  for (const line of lines) {
    const index = line.index;
    const split = splitOf(line);
    if (
      index === undefined ||
      split === ComandaSplit.None ||
      line.canceledAt ||
      line.amount <= 0
    ) {
      continue;
    }

    if (split === ComandaSplit.Line) {
      // The line as received: a delta run prints the remaining units as one
      // document (`2x Name` after 1 of 3 printed).
      separate.push({
        line,
        index,
        units: line.amount,
        marker: null,
        keySuffix: `${index}.L`,
      });
      continue;
    }

    // A delta run (copy amount < original) continues the numbering where the
    // printed units left off; a full-line run (first print, confirmed
    // reprint, group print) starts at 1.
    const total = Math.max(originalAmountOf(line), line.amount);
    const offset = total - line.amount;

    for (let k = 1; k <= line.amount; k++) {
      const unitNo = offset + k;
      separate.push({
        line: { ...line, amount: 1 },
        index,
        units: 1,
        marker: `${unitNo}/${total}`,
        keySuffix: `${index}.${unitNo}`,
      });
    }
  }

  return { grouped, separate };
}

/**
 * `${baseKey}:${keySuffix}` — one bridge job per physical document (sticker
 * precedent): a shared key would make the bridge's 90 s dedupe window swallow
 * every document after the first. `L` is never a digit, so a line key can
 * never collide with a unit key of the same index.
 */
export function comandaDocDedupeKey(
  baseKey: string,
  doc: IComandaSeparateDoc
): string {
  return `${baseKey}:${doc.keySuffix}`;
}

/**
 * Carries partial progress out of a mid-batch comanda failure: unlike a
 * single comanda document, a split batch is one job PER DOCUMENT, so the
 * units that DID print must be marked before the error propagates. Like
 * StickerBatchError it deliberately has NO retrySafety of its own, so
 * hasUnsafeRetrySafety(error) is false and every consumer treats the
 * never-attempted remainder as retryable; sites that need the failed
 * document's own class inspect `cause` explicitly.
 */
export class ComandaBatchError extends Error {
  readonly cause: unknown;
  /** Units of documents CONFIRMED printed in this run, by order-item index. */
  readonly printedUnits: ReadonlyMap<number, number>;
  /**
   * Units of the document that was in flight when the batch failed (grouped
   * document: every grouped line's amount; unit document: `{ index: 1 }`;
   * line document: `{ index: passed amount }`).
   */
  readonly inFlightUnits: ReadonlyMap<number, number>;
  /** Jobs-tab title of the failed document. */
  readonly failedDocTitle: string;

  constructor(
    cause: unknown,
    printedUnits: ReadonlyMap<number, number>,
    inFlightUnits: ReadonlyMap<number, number>,
    failedDocTitle: string
  ) {
    super(cause instanceof Error ? cause.message : 'Error imprimiendo comanda');
    this.name = 'ComandaBatchError';
    this.cause = cause;
    this.printedUnits = printedUnits;
    this.inFlightUnits = inFlightUnits;
    this.failedDocTitle = failedDocTitle;
  }
}

/**
 * Units to credit after a batch failure: the confirmed ones plus, when the
 * cause is unsafe/unknown (the in-flight document MAY have printed), the
 * in-flight ones — the document must never be silently re-printed.
 */
export function creditedComandaUnits(
  error: ComandaBatchError,
  causeMayHavePrinted: boolean
): Map<number, number> {
  const credited = new Map<number, number>(error.printedUnits);

  if (causeMayHavePrinted) {
    for (const [index, units] of error.inFlightUnits) {
      credited.set(index, (credited.get(index) ?? 0) + units);
    }
  }

  return credited;
}
